/**
 * Merchant-side auth helper.
 *
 * Two credential modes are supported (Phase 40):
 *
 *   • LEGACY  — long-lived `orbit_live_<prefix>_<secret>` API token,
 *               stored under `orbit-merchant-api-token`. Treated as
 *               OWNER on the server. Kept as a fallback for the
 *               cashier device + smoke flows.
 *
 *   • SESSION — short JWT access token + opaque refresh token issued
 *               by `POST /tenant/auth/login` for a real tenant user
 *               (Owner / Manager / Staff). Stored under
 *               `orbit-merchant-session`; the wrapper auto-refreshes
 *               on 401 and falls back to logout if the refresh fails.
 */
const TOKEN_KEY = "orbit-merchant-api-token";
const SESSION_KEY = "orbit-merchant-session";

export type MerchantRole = "OWNER" | "MANAGER" | "STAFF";

export interface MerchantSession {
  accessToken: string;
  accessTokenExpiresAt: string;
  refreshToken: string;
  refreshTokenExpiresAt: string;
  user: {
    id: string;
    tenantId: string;
    email: string;
    role: MerchantRole;
    mustSetPassword: boolean;
  };
  tenant: { id: string; name: string };
}

export function getMerchantToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setMerchantToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.removeItem(SESSION_KEY);
  window.dispatchEvent(new Event("orbit:merchant-auth-changed"));
}

export function getMerchantSession(): MerchantSession | null {
  const raw = localStorage.getItem(SESSION_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as MerchantSession;
  } catch {
    localStorage.removeItem(SESSION_KEY);
    return null;
  }
}

export function setMerchantSession(s: MerchantSession): void {
  localStorage.setItem(SESSION_KEY, JSON.stringify(s));
  // Sessions and legacy tokens are mutually exclusive — clear the
  // legacy one so the wrapper picks the JWT bearer.
  localStorage.removeItem(TOKEN_KEY);
  window.dispatchEvent(new Event("orbit:merchant-auth-changed"));
}

export function clearMerchantToken(): void {
  const session = getMerchantSession();
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(SESSION_KEY);
  window.dispatchEvent(new Event("orbit:merchant-auth-changed"));
  // Best-effort revoke; ignore failures.
  if (session?.refreshToken) {
    fetch("/api/v1/tenant/auth/logout", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ refreshToken: session.refreshToken }),
    }).catch(() => undefined);
  }
}

export function hasMerchantCredential(): boolean {
  return Boolean(getMerchantToken() || getMerchantSession());
}

export interface ApiOk<T> {
  ok: true;
  data: T;
}
export interface ApiErr {
  ok: false;
  status: number;
  code: string;
  message: string;
  details?: unknown;
}

function activeBearer(): string | null {
  const session = getMerchantSession();
  if (session) return session.accessToken;
  return getMerchantToken();
}

async function tryRefresh(): Promise<boolean> {
  const session = getMerchantSession();
  if (!session) return false;
  try {
    const res = await fetch("/api/v1/tenant/auth/refresh", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ refreshToken: session.refreshToken }),
    });
    if (!res.ok) return false;
    const json = (await res.json()) as { data?: MerchantSession };
    if (!json?.data) return false;
    setMerchantSession(json.data);
    return true;
  } catch {
    return false;
  }
}

export async function merchantFetch<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<ApiOk<T> | ApiErr> {
  async function once(): Promise<Response> {
    const headers = new Headers(init.headers);
    headers.set("accept", "application/json");
    if (init.body && !headers.has("content-type")) {
      headers.set("content-type", "application/json");
    }
    const bearer = activeBearer();
    if (bearer) headers.set("authorization", `Bearer ${bearer}`);
    return fetch(path, { ...init, headers });
  }

  let res = await once();
  // Auto-refresh on 401 once, but only when we have a session (legacy
  // tokens have no refresh path).
  if (res.status === 401 && getMerchantSession()) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      res = await once();
    }
  }

  let json: unknown = null;
  try {
    json = await res.json();
  } catch {
    /* non-JSON response */
  }

  if (res.status === 401) {
    clearMerchantToken();
  }

  if (res.ok) {
    const env = (json ?? {}) as { data?: T };
    return { ok: true, data: env.data as T };
  }
  const env = (json ?? {}) as {
    error?: { code?: string; message?: string; details?: unknown };
  };
  return {
    ok: false,
    status: res.status,
    code: env.error?.code ?? "ERROR",
    message: env.error?.message ?? `HTTP ${res.status}`,
    details: env.error?.details,
  };
}
